import type { Anamnesis, ParQFormData, Student, SubmitParQPayload } from '../../../types';
import { PARQ_QUESTIONS, type ParqBooleanFormField } from '../../../constants/parqQuestions';
import { maskCpf } from '../../../utils/masks';
import { validateEmail } from '../../../utils/validation';

const FOCUSABLE_FIELD_SELECTOR = 'input:not([type="hidden"]), select, textarea, button';

export interface PublicParqIdentificationState {
  studentName: string;
  email: string;
  cpf: string;
}

export interface PublicParqLockedFields {
  studentName: boolean;
  email: boolean;
  cpf: boolean;
}

const PARQ_REQUIRED_FIELDS: ParqBooleanFormField[] = PARQ_QUESTIONS.map((question) => question.field);

export const createEmptyPublicParqLockedFields = (): PublicParqLockedFields => ({
  studentName: false,
  email: false,
  cpf: false,
});

export const createInitialPublicParqIdentification = (): PublicParqIdentificationState => ({
  studentName: '',
  email: '',
  cpf: '',
});

export const createInitialPublicParqForm = (): ParQFormData => ({
  heartCondition: null,
  heartConditionOther: '',
  chestPainActivity: null,
  chestPainRecent: null,
  dizziness: null,
  boneJointProblem: null,
  takesBpMedication: null,
  otherReason: null,
  responsibility: '',
});

export const resolvePublicParqLockedFields = (student: Student): PublicParqLockedFields => ({
  studentName: true,
  email: true,
  cpf: true,
});

export const mapStudentToPublicParqIdentification = (student: Student): PublicParqIdentificationState => ({
  studentName: student.full_name?.trim() ?? '',
  email: student.email?.trim() ?? '',
  cpf: student.cpf ? maskCpf(student.cpf) : '',
});

export const mapAnamnesisToPublicParqForm = (anamnesis: Anamnesis | null): ParQFormData => {
  const form = createInitialPublicParqForm();

  PARQ_QUESTIONS.forEach((question) => {
    form[question.field] = anamnesis?.[question.dbColumn] ?? null;
  });

  form.heartConditionOther = anamnesis?.parq_heart_condition_other?.trim() ?? '';
  form.responsibility = anamnesis?.parq_responsibility?.trim() ?? '';

  return form;
};

export const getPublicParqFieldElementId = (field: string): string => `public-parq-${field.replace(/([A-Z])/g, '-$1').toLowerCase()}`;

export const PUBLIC_PARQ_FIELD_ELEMENT_IDS: Record<string, string> = {
  studentName: getPublicParqFieldElementId('studentName'),
  email: getPublicParqFieldElementId('email'),
  ...Object.fromEntries(
    PARQ_QUESTIONS.flatMap((question) => {
      const entries: Array<[string, string]> = [[question.field, getPublicParqFieldElementId(question.field)]];
      if (question.detailFormField) {
        entries.push([question.detailFormField, getPublicParqFieldElementId(question.detailFormField)]);
      }
      return entries;
    }),
  ),
};

export const PUBLIC_PARQ_FIELD_ORDER = [
  'studentName',
  'email',
  ...PARQ_QUESTIONS.flatMap((question) => (
    question.detailFormField ? [question.field, question.detailFormField] : [question.field]
  )),
];

export const sanitizePublicParqForm = (data: ParQFormData): ParQFormData => {
  const nextData = { ...data };

  PARQ_QUESTIONS.forEach((question) => {
    if (question.detailFormField && nextData[question.field] !== true) {
      nextData[question.detailFormField] = '';
    }
  });

  return nextData;
};

export const validatePublicParqIdentification = (
  identification: PublicParqIdentificationState,
  hasStudentToken: boolean,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const trimmedName = identification.studentName.trim();
  const trimmedEmail = identification.email.trim();

  if (!hasStudentToken) {
    if (!trimmedName) {
      errors.studentName = 'Informe o nome da aluna.';
    }

    if (!trimmedEmail) {
      errors.email = 'Informe o e-mail da aluna.';
    } else {
      const emailError = validateEmail(trimmedEmail);
      if (emailError) {
        errors.email = emailError;
      }
    }
  }

  return errors;
};

export const validatePublicParqForm = (
  identification: PublicParqIdentificationState,
  parq: ParQFormData,
  hasStudentToken: boolean,
): Record<string, string> => {
  const errors = validatePublicParqIdentification(identification, hasStudentToken);
  const sanitizedParq = sanitizePublicParqForm(parq);

  PARQ_REQUIRED_FIELDS.forEach((field) => {
    if (sanitizedParq[field] === null) {
      errors[field] = 'Selecione Sim ou Não.';
    }
  });

  PARQ_QUESTIONS.forEach((question) => {
    if (!question.detailFormField || sanitizedParq[question.field] !== true) {
      return;
    }

    const detailValue = sanitizedParq[question.detailFormField]?.trim() ?? '';
    if (!detailValue) {
      errors[question.detailFormField] = question.detailRequiredMessage ?? 'Preencha os detalhes.';
    }
  });

  // Declaração de responsabilidade temporariamente desabilitada
  // if (sanitizedParq.responsibility !== 'ciente') {
  //   errors.responsibility = 'Confirme a declaração de responsabilidade.';
  // }

  return errors;
};

const focusFieldContainer = (container: HTMLElement): void => {
  container.scrollIntoView({ behavior: 'smooth', block: 'center' });

  if (container.matches(FOCUSABLE_FIELD_SELECTOR)) {
    container.focus({ preventScroll: true });
    return;
  }

  const focusable = container.querySelector<HTMLElement>(FOCUSABLE_FIELD_SELECTOR);
  focusable?.focus({ preventScroll: true });
};

export const focusNearestPublicParqError = (errors: Record<string, string>): void => {
  const firstErrorField = PUBLIC_PARQ_FIELD_ORDER.find((field) => errors[field]);
  if (!firstErrorField) {
    return;
  }

  const elementId = PUBLIC_PARQ_FIELD_ELEMENT_IDS[firstErrorField];
  const container = elementId ? document.getElementById(elementId) : null;
  if (!container) {
    return;
  }

  focusFieldContainer(container);
};

export const buildPublicParqPayload = (
  identification: PublicParqIdentificationState,
  parq: ParQFormData,
  options: { studentId?: number; studentToken?: string } = {},
): SubmitParQPayload => {
  const sanitizedParq = sanitizePublicParqForm(parq);
  const trimmedEmail = identification.email.trim();

  return {
    studentId: options.studentId,
    studentToken: options.studentToken,
    studentName: identification.studentName.trim(),
    email: trimmedEmail || undefined,
    ...sanitizedParq,
  };
};
